import { z } from 'zod';

/**
 * Extracts the inner type of a non-primitive zod schema on a type level.
 */
export type Unwrap<Schema extends z.ZodTypeAny> = Schema extends
    | z.ZodOptional<infer S>
    | z.ZodNullable<infer S>
    | z.ZodDefault<infer S>
    | z.ZodBranded<infer S, keyof object>
    | z.ZodEffects<infer S, unknown, unknown>
    | z.ZodRecord<z.KeySchema, infer S>
    ? Unwrap<S>
    : Schema;

/**
 * Extracts the inner type of a non-primitive zod schema in runtime.
 */
export const unwrap = <Schema extends z.ZodTypeAny>(schema: Schema): Unwrap<Schema> => {
    switch (true) {
        case schema instanceof z.ZodOptional:
        case schema instanceof z.ZodNullable:
        case schema instanceof z.ZodDefault:
            return unwrap(schema._def.innerType);
        case schema instanceof z.ZodBranded:
            return unwrap(schema._def.type);
        case schema instanceof z.ZodEffects:
            return unwrap(schema._def.schema);
        case schema instanceof z.ZodRecord:
            return unwrap(schema.valueSchema);
        default:
            return schema as Unwrap<Schema>;
    }
};
